import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../AuthContext';
import { useNotifications } from '../NotificationContext';
import { useNavigate } from 'react-router-dom';
import { Check, X, AlertCircle, Filter, RefreshCw, Calendar, Clock, User, Plane, Briefcase, Edit2, Coffee, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import API_URL from '../config';

const API_BASE = API_URL;

// ── Cache & Performance helpers ───────────────────────────────────────────
const CACHE_KEY = 'demandes_page_cache_v1';

function loadCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveCache(data) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
}

// ── Pagination component ──────────────────────────────────────────────────
function PaginationControls({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  setItemsPerPage,
  onPageChange,
}) {
  if (totalItems <= 20 && itemsPerPage === 25) return null;

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * (itemsPerPage === 'all' ? totalItems : itemsPerPage) + 1;
  const endItem = itemsPerPage === 'all' ? totalItems : Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-gray-100 mt-4 text-xs text-gray-600">
      <div className="flex flex-wrap items-center gap-3">
        <span>
          Affichage <strong className="text-gray-900">{startItem}</strong> à <strong className="text-gray-900">{endItem}</strong> sur <strong className="text-gray-900">{totalItems}</strong>
        </span>
        <span className="text-gray-300">|</span>
        <div className="flex items-center gap-1.5">
          <span className="text-gray-500">Par page :</span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              const val = e.target.value === 'all' ? 'all' : Number(e.target.value);
              setItemsPerPage(val);
              onPageChange(1);
            }}
            className="px-2 py-1 bg-white border border-gray-200 rounded text-xs font-medium focus:ring-1 focus:ring-indigo-500 cursor-pointer"
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value="all">Tout</option>
          </select>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Page précédente"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
            .reduce((acc, p, idx, arr) => {
              if (idx > 0 && p - arr[idx - 1] > 1) {
                acc.push(-1);
              }
              acc.push(p);
              return acc;
            }, [])
            .map((p, idx) =>
              p === -1 ? (
                <span key={`ell-${idx}`} className="px-1.5 py-1 text-gray-400">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => onPageChange(p)}
                  className={`min-w-[30px] h-7 px-2 rounded-lg font-medium text-xs transition-colors ${
                    currentPage === p
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'border border-gray-200 hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  {p}
                </button>
              )
            )}

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Page suivante"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Leave datetime helpers ────────────────────────────────────────────────
function getLeavePeriod(start_datetime, end_datetime) {
  const startHour = new Date(start_datetime).getHours();
  const endHour   = new Date(end_datetime).getHours();
  const sameDay   = start_datetime.slice(0,10) === end_datetime.slice(0,10);
  if (sameDay) {
    if (startHour === 0  && endHour === 12) return 'morning';
    if (startHour === 12 && endHour !== 12) return 'afternoon';
  }
  return 'full';
}

const LEAVE_TYPES = [
  { value: 'paid_leave', label: 'Congés payés (CP)', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  { value: 'sick_leave', label: 'Arrêt maladie', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'work_accident', label: 'Accident (AT)', color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'unjustified', label: 'Absence', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  { value: 'compensatory', label: 'Repos (HS)', color: 'bg-purple-100 text-purple-700 border-purple-200' },
];

const getLeaveTypeConfig = (value) => LEAVE_TYPES.find(t => t.value === value) || { label: value, color: 'bg-gray-100 text-gray-700 border-gray-200' };

const LeaveTypeBadge = ({ type }) => {
  const cfg = getLeaveTypeConfig(type);
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
      {cfg.label}
    </span>
  );
};

// ── Shift difference calculation helpers ──────────────────────────────────
function calculateShiftDifferences(shift, request) {
  if (!request?.start_time || !request?.end_time) return null;

  const reqStart = new Date(request.start_time);
  const reqEnd = new Date(request.end_time);
  if (isNaN(reqStart.getTime()) || isNaN(reqEnd.getTime())) return null;

  const reqGrossMin = Math.round((reqEnd.getTime() - reqStart.getTime()) / 60000);
  const origBreak = shift ? (Number(shift.break_duration) || 0) : 0;
  const hasReqBreak = request.break_duration !== null && request.break_duration !== undefined;
  const reqBreak = hasReqBreak ? (Number(request.break_duration) || 0) : origBreak;

  const diffBreak = reqBreak - origBreak;
  const reqNetMin = Math.max(0, reqGrossMin - reqBreak);

  let diffWorkMin = null;
  let origNetMin = null;

  if (shift?.start_time && shift?.end_time) {
    const origStart = new Date(shift.start_time);
    const origEnd = new Date(shift.end_time);
    if (!isNaN(origStart.getTime()) && !isNaN(origEnd.getTime())) {
      const origGrossMin = Math.round((origEnd.getTime() - origStart.getTime()) / 60000);
      origNetMin = Math.max(0, origGrossMin - origBreak);
      diffWorkMin = reqNetMin - origNetMin;
    }
  }

  return {
    diffWorkMin,
    diffBreak,
    hasBreakDiff: diffBreak !== 0,
    reqNetMin,
    origNetMin,
    reqBreak,
    origBreak,
  };
}

function formatDiffMinutes(minutes) {
  if (minutes === 0) return '0 min';
  const sign = minutes > 0 ? '+' : '-';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (h === 0) return `${sign}${m} min`;
  if (m === 0) return `${sign}${h}h`;
  return `${sign}${h}h${m < 10 ? '0' : ''}${m}`;
}

function formatDurationMinutes(minutes) {
  if (minutes === null || minutes === undefined || isNaN(minutes)) return '—';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h}h`;
  return `${h}h${m < 10 ? '0' : ''}${m}`;
}

const ShiftDifferenceBadge = ({ diff }) => {
  if (!diff) return <span className="text-sm text-gray-400">—</span>;

  return (
    <div className="flex flex-col gap-1 items-start">
      {/* Différence de travail demandée */}
      {diff.diffWorkMin !== null ? (
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
            diff.diffWorkMin > 0
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              : diff.diffWorkMin < 0
              ? 'bg-rose-50 text-rose-700 border border-rose-200'
              : 'bg-gray-100 text-gray-700 border border-gray-200'
          }`}
          title={`Travail net : ${formatDurationMinutes(diff.origNetMin)} → ${formatDurationMinutes(diff.reqNetMin)}`}
        >
          <Clock className="w-3 h-3" />
          <span>Travail : {formatDiffMinutes(diff.diffWorkMin)}</span>
        </span>
      ) : (
        <span className="text-xs text-gray-600 font-medium">
          Travail : {formatDurationMinutes(diff.reqNetMin)}
        </span>
      )}

      {/* Différence de pause (si il y en a une) */}
      {diff.hasBreakDiff && (
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
            diff.diffBreak > 0
              ? 'bg-amber-50 text-amber-700 border border-amber-200'
              : 'bg-blue-50 text-blue-700 border border-blue-200'
          }`}
          title={`Pause : ${diff.origBreak} min → ${diff.reqBreak} min`}
        >
          <Coffee className="w-3 h-3" />
          <span>Pause : {formatDiffMinutes(diff.diffBreak)}</span>
        </span>
      )}
    </div>
  );
};

export default function Demandes() {
  const { isManager } = useAuth();
  const navigate = useNavigate();
  const { triggerRefresh } = useNotifications();
  
  const cachedData = useMemo(() => loadCache(), []);

  // Separate states for shift and leave requests
  const [shiftRequests, setShiftRequests] = useState(cachedData?.shiftRequests || []);
  const [leaveRequests, setLeaveRequests] = useState(cachedData?.leaveRequests || []);
  const [users, setUsers] = useState(cachedData?.users || {});
  const [shifts, setShifts] = useState(cachedData?.shifts || {});

  const [loading, setLoading] = useState(!cachedData);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [requestType, setRequestType] = useState('all'); // all, shifts, leaves
  const [statusFilter, setStatusFilter] = useState('pending'); // all, pending, approved, rejected
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [shiftPage, setShiftPage] = useState(1);
  const [leavePage, setLeavePage] = useState(1);
  const [actionLoading, setActionLoading] = useState(null);

  // States for ShiftActionModal
  const [rooms, setRooms] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [shiftActionModal, setShiftActionModal] = useState({
    show: false,
    shift: null,
    mode: null,
  });
  const [editShift, setEditShift] = useState({
    user_id: "",
    start_datetime: "",
    end_datetime: "",
    no_show: false,
    room_id: "",
    break_duration: 0,
    comment: "",
  });
  // Redirect if not manager
  useEffect(() => {
    if (!isManager) {
      navigate('/gestionShifts');
    }
  }, [isManager, navigate]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/users/`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) return {};

      const data = await response.json();
      const userMap = {};
      data.forEach(u => userMap[u.id] = u);
      setUsers(userMap);
      return userMap;
    } catch (error) {
      console.error('Error fetching users:', error);
      return {};
    }
  };

  const fetchShiftRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/requests/?limit=1000`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Erreur lors de la récupération des demandes de shifts');

      const data = await response.json();
      setShiftRequests(data);

      const shiftMap = {};
      data.forEach(req => {
        if (req.shift) shiftMap[req.shift_id] = req.shift;
      });
      setShifts(prev => ({ ...prev, ...shiftMap }));
      return { requests: data, shifts: shiftMap };
    } catch (error) {
      console.error('Error fetching shift requests:', error);
      return { requests: [], shifts: {} };
    }
  };

  const fetchLeaveRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/leaves/team-requests`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Erreur lors de la récupération des demandes de congés');

      const data = await response.json();
      setLeaveRequests(data);
      return data;
    } catch (error) {
      console.error('Error fetching leave requests:', error);
      return [];
    }
  };

  const fetchAllRequests = async (showFullLoader = false) => {
    if (showFullLoader) setLoading(true);
    setIsRefreshing(true);
    try {
      const [uMap, sData, lData] = await Promise.all([
        fetchUsers(),
        fetchShiftRequests(),
        fetchLeaveRequests(),
      ]);

      saveCache({
        users: uMap,
        shiftRequests: sData.requests,
        shifts: sData.shifts,
        leaveRequests: lData,
      });
    } catch (error) {
      console.error('Error fetching requests:', error);
      if (showFullLoader) alert('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Chargement des rooms et tags une seule fois au montage
  useEffect(() => {
    if (!isManager) return;
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/rooms/`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : [])
      .then(setRooms)
      .catch(console.error);

    fetch(`${API_BASE}/tags/`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : [])
      .then(setTags)
      .catch(console.error);
  }, [isManager]);

  useEffect(() => {
    if (isManager) {
      const hasInitialData = shiftRequests.length > 0 || leaveRequests.length > 0;
      fetchAllRequests(!hasInitialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManager]);

  // Shift request handlers
  const handleApproveShift = async (requestId) => {
    setActionLoading(requestId);
    try {
      const token = localStorage.getItem('token');

      // 1. Approuver la demande (met également à jour les horaires du shift)
      const response = await fetch(`${API_BASE}/requests/${requestId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de l\'approbation');
      }

      const approvedRequest = await response.json();

      // 2. Valider le shift associé
      if (approvedRequest?.shift_id) {
        try {
          await fetch(`${API_BASE}/shifts/${approvedRequest.shift_id}`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ validated: true }),
          });
        } catch (validateError) {
          console.warn('Shift approuvé mais non validé :', validateError);
        }
      }

      await fetchShiftRequests();
      triggerRefresh(); // Update notification count
    } catch (error) {
      console.error('Error approving shift request:', error);
      alert(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectShift = async (requestId) => {
    setActionLoading(requestId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/requests/${requestId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors du rejet');
      }

      await fetchShiftRequests();
      triggerRefresh(); // Update notification count
    } catch (error) {
      console.error('Error rejecting shift request:', error);
      alert(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleModifyShift = async (requestId, shiftId) => {
    setActionLoading(requestId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/requests/${requestId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors du rejet de la demande');
      }

      await fetchShiftRequests();
      triggerRefresh();
      
      // Show the modal directly in Demandes
      const shiftToEdit = shifts[shiftId];
      if (shiftToEdit) {
        setShiftActionModal({
          show: true,
          shift: { ...shiftToEdit, user_name: users[shiftToEdit.user_id]?.full_name },
          mode: "edit"
        });
        setEditShift({
          user_id: shiftToEdit.user_id,
          start_datetime: shiftToEdit.start_time?.slice(0, 16) || "",
          end_datetime: shiftToEdit.end_time?.slice(0, 16) || "",
          no_show: shiftToEdit.no_show || false,
          room_id: shiftToEdit.room_id || "",
          break_duration: shiftToEdit.break_duration || 0,
          comment: shiftToEdit.comment || "",
        });
        setSelectedTagIds(shiftToEdit.tags?.map((t) => t.id) || []);
      }
    } catch (error) {
      console.error('Error rejecting shift request before modifying:', error);
      alert(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateShift = async () => {
    setActionLoading("modal");
    try {
      const token = localStorage.getItem('token');
      const payload = {
        user_id: editShift.user_id,
        start_time: editShift.start_datetime?.length === 16 ? `${editShift.start_datetime}:00` : editShift.start_datetime,
        end_time: editShift.end_datetime?.length === 16 ? `${editShift.end_datetime}:00` : editShift.end_datetime,
        room_id: editShift.room_id ? parseInt(editShift.room_id) : null,
        no_show: editShift.no_show,
        break_duration: parseInt(editShift.break_duration) || 0,
        comment: editShift.comment || "",
        tag_ids: selectedTagIds,
      };

      const res = await fetch(`${API_BASE}/shifts/${shiftActionModal.shift.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de la modification");
      }

      await fetchShiftRequests();
      triggerRefresh();
      setShiftActionModal({ show: false, shift: null, mode: null });
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteShift = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce shift ?")) return;

    setActionLoading("modal");
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/shifts/${shiftActionModal.shift.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de la suppression");
      }

      await fetchShiftRequests();
      triggerRefresh();
      setShiftActionModal({ show: false, shift: null, mode: null });
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleValidateSingleShift = async (shiftId) => {
    setActionLoading("modal");
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE}/shifts/${shiftId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ validated: true }),
      });
      await fetchShiftRequests();
    } catch (error) {
      console.error('Error validating shift:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Leave request handlers
  const handleApproveLeave = async (leaveId) => {
    setActionLoading(`leave-${leaveId}`);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/leaves/${leaveId}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de l\'approbation');
      }

      await fetchLeaveRequests();
      triggerRefresh(); // Update notification count
    } catch (error) {
      console.error('Error approving leave request:', error);
      alert(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectLeave = async (leaveId) => {
    const comment = window.prompt('Raison du rejet (optionnel):');
    if (comment === null) return; // User cancelled

    setActionLoading(`leave-${leaveId}`);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/leaves/${leaveId}/reject`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ manager_comment: comment || 'Aucune raison fournie' }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors du rejet');
      }

      await fetchLeaveRequests();
      triggerRefresh(); // Update notification count
    } catch (error) {
      console.error('Error rejecting leave request:', error);
      alert(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Reset pages when filters change
  useEffect(() => {
    setShiftPage(1);
    setLeavePage(1);
  }, [statusFilter, selectedUserId, requestType, itemsPerPage]);

  // Nombre de demandes par utilisateur
  const userRequestCounts = useMemo(() => {
    const counts = {};
    shiftRequests.forEach((r) => {
      counts[r.user_id] = (counts[r.user_id] || 0) + 1;
    });
    leaveRequests.forEach((r) => {
      counts[r.user_id] = (counts[r.user_id] || 0) + 1;
    });
    return counts;
  }, [shiftRequests, leaveRequests]);

  // Utilisateurs triés avec ceux qui ont des demandes en premier
  const sortedUsers = useMemo(() => {
    return Object.values(users).sort((a, b) => {
      const countA = userRequestCounts[a.id] || 0;
      const countB = userRequestCounts[b.id] || 0;
      if (countA > 0 && countB === 0) return -1;
      if (countA === 0 && countB > 0) return 1;
      const nameA = (a.full_name || a.email || '').toLowerCase();
      const nameB = (b.full_name || b.email || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [users, userRequestCounts]);

  // Filter requests avec useMemo
  const filteredShiftRequests = useMemo(() => {
    return shiftRequests.filter((req) => {
      if (statusFilter !== 'all' && req.status !== statusFilter) return false;
      if (selectedUserId !== 'all' && String(req.user_id) !== String(selectedUserId)) return false;
      return true;
    });
  }, [shiftRequests, statusFilter, selectedUserId]);

  const filteredLeaveRequests = useMemo(() => {
    return leaveRequests.filter((req) => {
      if (statusFilter !== 'all' && req.status !== statusFilter) return false;
      if (selectedUserId !== 'all' && String(req.user_id) !== String(selectedUserId)) return false;
      return true;
    });
  }, [leaveRequests, statusFilter, selectedUserId]);

  // Pagination shifts
  const totalShiftPages = itemsPerPage === 'all' ? 1 : Math.ceil(filteredShiftRequests.length / itemsPerPage);
  const paginatedShiftRequests = useMemo(() => {
    if (itemsPerPage === 'all') return filteredShiftRequests;
    const start = (shiftPage - 1) * itemsPerPage;
    return filteredShiftRequests.slice(start, start + itemsPerPage);
  }, [filteredShiftRequests, shiftPage, itemsPerPage]);

  // Pagination congés
  const totalLeavePages = itemsPerPage === 'all' ? 1 : Math.ceil(filteredLeaveRequests.length / itemsPerPage);
  const paginatedLeaveRequests = useMemo(() => {
    if (itemsPerPage === 'all') return filteredLeaveRequests;
    const start = (leavePage - 1) * itemsPerPage;
    return filteredLeaveRequests.slice(start, start + itemsPerPage);
  }, [filteredLeaveRequests, leavePage, itemsPerPage]);

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-orange-100 text-orange-700 border-orange-200',
      approved: 'bg-green-100 text-green-700 border-green-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
    };
    const labels = {
      pending: 'En attente',
      approved: 'Approuvée',
      rejected: 'Rejetée',
    };
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border-2 ${badges[status]}`}>
        <AlertCircle className="w-3 h-3" />
        {labels[status]}
      </span>
    );
  };

  const getUserName = (userId) => {
    const u = users[userId];
    return u?.full_name || u?.email || `User ${userId}`;
  };

  if (!isManager) {
    return null;
  }

  const showShifts = requestType === 'all' || requestType === 'shifts';
  const showLeaves = requestType === 'all' || requestType === 'leaves';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Gestion des Demandes
              </h1>
              <p className="text-gray-600 mt-1">
                Gérez toutes les demandes de l'équipe
              </p>
            </div>
            <button
              onClick={() => fetchAllRequests(false)}
              disabled={isRefreshing || loading}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing || loading ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Actualisation...' : 'Actualiser'}
            </button>
          </div>

          {/* Request Type Filters */}
          <div className="flex flex-wrap gap-2 mt-4">
            <button
              onClick={() => setRequestType('all')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                requestType === 'all'
                  ? 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Filter className="w-4 h-4" />
              Toutes ({shiftRequests.length + leaveRequests.length})
            </button>
            <button
              onClick={() => setRequestType('shifts')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                requestType === 'shifts'
                  ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Briefcase className="w-4 h-4" />
              Demandes de Shifts ({shiftRequests.length})
            </button>
            <button
              onClick={() => setRequestType('leaves')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                requestType === 'leaves'
                  ? 'bg-purple-100 text-purple-700 ring-2 ring-purple-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Plane className="w-4 h-4" />
              Demandes de Congés ({leaveRequests.length})
            </button>
          </div>

          {/* Status Filters */}
          <div className="flex flex-wrap gap-2 mt-3">
            {['all', 'pending', 'approved', 'rejected'].map((f) => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  statusFilter === f
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f === 'all' ? 'Tous statuts' : f === 'pending' ? 'En attente' : f === 'approved' ? 'Approuvées' : 'Rejetées'}
              </button>
            ))}
          </div>

          {/* User Filter Section */}
          <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-semibold text-gray-700">Filtrer par employé :</span>
            </div>
            <div className="relative min-w-[260px] max-w-sm flex-1">
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="w-full px-3 py-2 pl-9 bg-gray-50 hover:bg-gray-100 border border-gray-300 rounded-lg text-sm font-medium text-gray-800 focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all cursor-pointer"
              >
                <option value="all">
                  Tous les employés ({shiftRequests.length + leaveRequests.length} demandes au total)
                </option>
                {sortedUsers.map((u) => {
                  const count = userRequestCounts[u.id] || 0;
                  return (
                    <option key={u.id} value={u.id}>
                      {u.full_name || u.email} {count > 0 ? `(${count} demande${count > 1 ? 's' : ''})` : '(0)'}
                    </option>
                  );
                })}
              </select>
              <User className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>

            {selectedUserId !== 'all' && (
              <button
                onClick={() => setSelectedUserId('all')}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 rounded-lg text-xs font-semibold transition-all"
                title="Voir tous les employés"
              >
                <X className="w-3.5 h-3.5" />
                <span>Réinitialiser (voir tout le monde)</span>
              </button>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        )}

        {/* Shift Requests Section */}
        {!loading && showShifts && filteredShiftRequests.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-blue-600" />
                Demandes de Modification de Shifts ({filteredShiftRequests.length})
                {selectedUserId !== 'all' && (
                  <span className="text-xs font-normal text-indigo-700 bg-indigo-50 border border-indigo-200 px-2.5 py-0.5 rounded-full">
                    Employé : {getUserName(selectedUserId)}
                  </span>
                )}
              </h2>
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employé</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Horaires actuels</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Horaires demandés</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Différence demandée</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Raison</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedShiftRequests.map((request) => {
                    const shift = shifts[request.shift_id] || request.shift;
                    const diff = calculateShiftDifferences(shift, request);
                    return (
                      <tr key={request.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <User className="w-5 h-5 text-gray-400 mr-2" />
                            <span className="text-sm font-medium text-gray-900">
                              {getUserName(request.user_id)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="w-4 h-4 mr-2" />
                            {new Date(request.start_time).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center text-sm text-gray-600">
                              <Clock className="w-4 h-4 mr-2" />
                              {shift?.start_time?.slice(11, 16) || '??:??'} - {shift?.end_time?.slice(11, 16) || '??:??'}
                            </div>
                            {shift?.break_duration > 0 && (
                              <div className="text-xs text-gray-500 pl-6">
                                Pause : {shift.break_duration} min
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center text-sm font-medium text-indigo-600">
                              <Clock className="w-4 h-4 mr-2" />
                              {request.start_time?.slice(11, 16)} - {request.end_time?.slice(11, 16)}
                            </div>
                            {request.break_duration !== null && request.break_duration !== undefined && (
                              <div className="text-xs text-indigo-500 pl-6">
                                Pause : {request.break_duration} min
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <ShiftDifferenceBadge diff={diff} />
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-600">
                            {request.reason || '—'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(request.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {request.status === 'pending' && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleRejectShift(request.id)}
                                disabled={actionLoading === request.id}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                title="Refuser"
                              >
                                <X className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleModifyShift(request.id, request.shift_id)}
                                disabled={actionLoading === request.id}
                                className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50"
                                title="Modifier"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleApproveShift(request.id)}
                                disabled={actionLoading === request.id}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                                title="Valider"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {paginatedShiftRequests.map((request) => {
                const shift = shifts[request.shift_id] || request.shift;
                const diff = calculateShiftDifferences(shift, request);
                return (
                  <div key={request.id} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <User className="w-5 h-5 text-gray-400" />
                        <span className="font-medium text-gray-900">{getUserName(request.user_id)}</span>
                      </div>
                      {getStatusBadge(request.status)}
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                      <Calendar className="w-4 h-4" />
                      {new Date(request.start_time).toLocaleDateString('fr-FR', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long'
                      })}
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1">Horaires actuels</div>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1 text-sm font-medium text-gray-800">
                            <Clock className="w-3 h-3" />
                            {shift?.start_time?.slice(11, 16) || '??:??'} - {shift?.end_time?.slice(11, 16) || '??:??'}
                          </div>
                          {shift?.break_duration > 0 && (
                            <div className="text-xs text-gray-500 pl-4">
                              Pause : {shift.break_duration} min
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="bg-indigo-50 rounded-lg p-3">
                        <div className="text-xs text-indigo-600 mb-1">Horaires demandés</div>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1 text-sm font-medium text-indigo-700">
                            <Clock className="w-3 h-3" />
                            {request.start_time?.slice(11, 16)} - {request.end_time?.slice(11, 16)}
                          </div>
                          {request.break_duration !== null && request.break_duration !== undefined && (
                            <div className="text-xs text-indigo-500 pl-4">
                              Pause : {request.break_duration} min
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {diff && (
                      <div className="bg-gray-50 border border-gray-200/80 rounded-lg p-2.5 mb-3 flex items-center justify-between gap-2">
                        <span className="text-xs text-gray-500 font-medium">Différence :</span>
                        <ShiftDifferenceBadge diff={diff} />
                      </div>
                    )}

                    {request.reason && (
                      <div className="bg-gray-50 rounded-lg p-3 mb-3">
                        <div className="text-xs text-gray-500 mb-1">Raison</div>
                        <div className="text-sm text-gray-700">{request.reason}</div>
                      </div>
                    )}

                    {request.status === 'pending' && (
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={() => handleRejectShift(request.id)}
                          disabled={actionLoading === request.id}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50"
                        >
                          <X className="w-4 h-4" />
                          Refuser
                        </button>
                        <button
                          onClick={() => handleModifyShift(request.id, request.shift_id)}
                          disabled={actionLoading === request.id}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors disabled:opacity-50"
                        >
                          <Edit2 className="w-4 h-4" />
                          Modifier
                        </button>
                        <button
                          onClick={() => handleApproveShift(request.id)}
                          disabled={actionLoading === request.id}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                        >
                          <Check className="w-4 h-4" />
                          Valider
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination Shifts */}
            <PaginationControls
              currentPage={shiftPage}
              totalPages={totalShiftPages}
              totalItems={filteredShiftRequests.length}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              onPageChange={setShiftPage}
            />
          </div>
        )}

        {/* Leave Requests Section */}
        {!loading && showLeaves && filteredLeaveRequests.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Plane className="w-5 h-5 text-purple-600" />
                Demandes de Congés ({filteredLeaveRequests.length})
                {selectedUserId !== 'all' && (
                  <span className="text-xs font-normal text-purple-700 bg-purple-50 border border-purple-200 px-2.5 py-0.5 rounded-full">
                    Employé : {getUserName(selectedUserId)}
                  </span>
                )}
              </h2>
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employé</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Jours</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Raison</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedLeaveRequests.map((request) => (
                    <tr key={request.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="w-5 h-5 text-gray-400 mr-2" />
                          <span className="text-sm font-medium text-gray-900">
                            {getUserName(request.user_id)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1 items-start">
                          <LeaveTypeBadge type={request.leave_type} />
                          {request.is_anticipated && <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-800 border border-orange-200">Anticipé</span>}
                          {request.start_datetime && request.end_datetime && getLeavePeriod(request.start_datetime, request.end_datetime) !== 'full' && (
                            <span className="text-xs text-gray-500">{getLeavePeriod(request.start_datetime, request.end_datetime) === 'morning' ? '½j matin' : '½j après-midi'}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar className="w-4 h-4 mr-2" />
                          {new Date(request.start_datetime ? request.start_datetime.slice(0,10) : request.start_date).toLocaleDateString('fr-FR')} - {new Date(request.end_datetime ? request.end_datetime.slice(0,10) : request.end_date).toLocaleDateString('fr-FR')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-purple-600">{request.days_count} jours</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">
                          {request.reason || '—'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(request.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {request.status === 'pending' && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleRejectLeave(request.id)}
                              disabled={actionLoading === `leave-${request.id}`}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Rejeter"
                            >
                              <X className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleApproveLeave(request.id)}
                              disabled={actionLoading === `leave-${request.id}`}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Approuver"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {paginatedLeaveRequests.map((request) => (
                <div key={request.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-5 h-5 text-gray-400" />
                        <span className="font-medium text-gray-900">{getUserName(request.user_id)}</span>
                      </div>
                      <LeaveTypeBadge type={request.leave_type} />
                      {request.is_anticipated && <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-800 border border-orange-200">Anticipé</span>}
                      {request.start_datetime && request.end_datetime && getLeavePeriod(request.start_datetime, request.end_datetime) !== 'full' && (
                        <span className="text-xs text-gray-500">{getLeavePeriod(request.start_datetime, request.end_datetime) === 'morning' ? '½j matin' : '½j après-midi'}</span>
                      )}
                    </div>
                    {getStatusBadge(request.status)}
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                    <Calendar className="w-4 h-4" />
                    {new Date(request.start_datetime ? request.start_datetime.slice(0,10) : request.start_date).toLocaleDateString('fr-FR')} - {new Date(request.end_datetime ? request.end_datetime.slice(0,10) : request.end_date).toLocaleDateString('fr-FR')}
                  </div>

                  <div className="flex items-center gap-2 text-sm font-medium text-purple-600 mb-3">
                    <Clock className="w-4 h-4" />
                    {request.days_count} jours
                  </div>

                  {request.reason && (
                    <div className="bg-gray-50 rounded-lg p-3 mb-3">
                      <div className="text-xs text-gray-500 mb-1">Raison</div>
                      <div className="text-sm text-gray-700">{request.reason}</div>
                    </div>
                  )}

                  {request.status === 'pending' && (
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => handleRejectLeave(request.id)}
                        disabled={actionLoading === `leave-${request.id}`}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50"
                      >
                        <X className="w-4 h-4" />
                        Rejeter
                      </button>
                      <button
                        onClick={() => handleApproveLeave(request.id)}
                        disabled={actionLoading === `leave-${request.id}`}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        <Check className="w-4 h-4" />
                        Approuver
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination Congés */}
            <PaginationControls
              currentPage={leavePage}
              totalPages={totalLeavePages}
              totalItems={filteredLeaveRequests.length}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              onPageChange={setLeavePage}
            />
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredShiftRequests.length === 0 && filteredLeaveRequests.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              Aucune demande
            </h3>
            <p className="text-gray-500">
              {selectedUserId !== 'all'
                ? `Aucune demande trouvée pour ${getUserName(selectedUserId)} avec les filtres sélectionnés.`
                : 'Aucune demande ne correspond aux filtres sélectionnés.'}
            </p>
            {selectedUserId !== 'all' && (
              <button
                onClick={() => setSelectedUserId('all')}
                className="mt-4 px-4 py-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg text-sm font-semibold transition-all inline-flex items-center gap-1.5"
              >
                <X className="w-4 h-4" />
                Réinitialiser le filtre employé
              </button>
            )}
          </div>
        )}
      </div>

      {/* Shift Action Modal */}
      {shiftActionModal.show && shiftActionModal.shift && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto transform transition-all">
            <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Modifier le shift
              </h2>
              <button
                onClick={() => setShiftActionModal({ show: false, shift: null, mode: null })}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center text-white font-semibold shadow-md">
                  {(shiftActionModal.shift.user_name || "?").charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="font-semibold text-gray-800">
                    {shiftActionModal.shift.user_name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(shiftActionModal.shift.start_time).toLocaleDateString("fr-FR", {
                      weekday: "long",
                      day: "numeric",
                      month: "long"
                    })}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employé</label>
                <select
                  value={editShift.user_id}
                  onChange={(e) => setEditShift({ ...editShift, user_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  {Object.values(users).map((u) => (
                    <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Début</label>
                  <input
                    type="datetime-local"
                    value={editShift.start_datetime}
                    onChange={(e) => setEditShift({ ...editShift, start_datetime: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fin</label>
                  <input
                    type="datetime-local"
                    value={editShift.end_datetime}
                    onChange={(e) => setEditShift({ ...editShift, end_datetime: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mt-4">
                <label className="block text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                  <Coffee className="w-4 h-4" />
                  Temps de pause (minutes)
                </label>
                <input
                  type="number"
                  min="0"
                  step="5"
                  value={editShift.break_duration}
                  onChange={(e) => setEditShift({ ...editShift, break_duration: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-400 bg-white"
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Commentaire
                </label>
                <textarea
                  value={editShift.comment || ""}
                  onChange={(e) => setEditShift({ ...editShift, comment: e.target.value })}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 resize-none"
                ></textarea>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Salle</label>
                <select
                  value={editShift.room_id}
                  onChange={(e) => setEditShift({ ...editShift, room_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Aucune salle</option>
                  {rooms.map((room) => (
                    <option key={room.id} value={room.id}>{room.name}</option>
                  ))}
                </select>
              </div>

              {tags.length > 0 && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Étiquettes</label>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => {
                          setSelectedTagIds(prev => prev.includes(tag.id) ? prev.filter(id => id !== tag.id) : [...prev, tag.id]);
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${selectedTagIds.includes(tag.id) ? 'ring-2 ring-offset-2 ring-indigo-500' : 'hover:opacity-80'}`}
                        style={{ backgroundColor: tag.color, color: '#ffffff' }}
                      >
                        {selectedTagIds.includes(tag.id) && <Check className="w-3 h-3 inline mr-1" />}
                        {tag.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4 flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Absence non justifiée</span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={editShift.no_show}
                  onClick={() => setEditShift({ ...editShift, no_show: !editShift.no_show })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 touch-manipulation ${editShift.no_show ? 'bg-red-600' : 'bg-gray-200'}`}
                >
                  <span className="sr-only">Toggle absence</span>
                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${editShift.no_show ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>

              <div className="flex gap-2 pt-3">
                <button
                  onClick={handleDeleteShift}
                  disabled={actionLoading === "modal"}
                  className="flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-medium rounded-lg bg-red-100 hover:bg-red-200 text-red-700 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Supprimer
                </button>
                {(() => {
                  const shiftEnd = new Date(shiftActionModal.shift.end_datetime || shiftActionModal.shift.end_time);
                  const isPast = shiftEnd < new Date();
                  if (isPast && !shiftActionModal.shift.validated) {
                    return (
                      <button
                        onClick={() => {
                          handleValidateSingleShift(shiftActionModal.shift.id);
                          setShiftActionModal({ show: false, shift: null, mode: null });
                        }}
                        disabled={actionLoading === "modal"}
                        className="flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-medium rounded-lg bg-green-100 hover:bg-green-200 text-green-700 transition-colors disabled:opacity-50"
                      >
                        <Check className="w-4 h-4" />
                        Valider
                      </button>
                    );
                  }
                  return null;
                })()}
                <button
                  onClick={handleUpdateShift}
                  disabled={actionLoading === "modal"}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-medium rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg disabled:opacity-50"
                >
                  <Edit2 className="w-4 h-4" />
                  {actionLoading === "modal" ? "Modification..." : "Modifier"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
